import { useEffect, useState } from "react";
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";

import { IconCalendar } from "@pentabd/icons";
import { Button, DateInput, Text } from "@pentabd/ui";

import {
  ErrorModal,
  FileRow,
  InputTextAreaRow,
  InputTextRow,
  SelectControllerRow,
  SuccessModal,
} from "@components";

import { useRoomConference } from "@hooks/inventory/roomConference/useRoomConference";
import { useMetaData } from "@hooks/inventory/roomConfiguration/useMetaData";
import { useLanguage } from "@hooks/useLanguage";

import { ROOM_META_DATA } from "@constants/inventory/room-meta-data";

import {
  ROOM_CONFERENCE,
  RoomConferenceSchemaType,
  roomConferenceValidationSchema,
} from "@validations/Inventory/roomConference/roomConferenceValidation";

import { getConvertedOfficeAddress } from "@helpers/baseDataConvert";
import { getConvertedMetaData } from "@helpers/inventory/metaDataConvert";

import { ROUTES } from "@constants/routes";
import {
  CreateConferenceRoomType,
  RoomConferenceResponseType,
} from "@type/inventory/room-conference";
import { convertSingleDocumentData } from "@utils/documentDataConvert";

const { INVENTORY } = ROUTES;
const { CONFERENCE_ROOM } = INVENTORY;

const { OFFICE, ROOM, BUILDING } = ROOM_META_DATA;

const CONFERENCE_ROOM_ID = 1;

export default function CreateRoomConferenceForm() {
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<boolean>(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);
  const [createErrorMessage, setCreateErrorMessage] = useState("");

  const { t } = useTranslation();
  const { language } = useLanguage();

  const navigate = useNavigate();

  const { data: office } = useMetaData(OFFICE);
  const { data: building, setQuery: setBuildingQueries } =
    useMetaData(BUILDING);
  const { data: room, setQuery: setRoomQueries } = useMetaData(ROOM);

  const { createRoomConference, roomConferenceListState } = useRoomConference();

  const { createError, createSuccess } =
    roomConferenceListState || ({} as RoomConferenceResponseType);

  const method = useForm<RoomConferenceSchemaType>({
    resolver: yupResolver(roomConferenceValidationSchema),
    mode: "onChange",
  });

  const {
    handleSubmit,
    register,
    setValue,
    control,
    watch,
    formState: { errors },
  } = method;

  const startDate = watch(ROOM_CONFERENCE.BOOKING_START_DATE);
  const endDate = watch(ROOM_CONFERENCE.BOOKING_END_DATE);

  const watchOffice = watch(ROOM_CONFERENCE.OFFICE);
  const watchBuilding = watch(ROOM_CONFERENCE.BUILDING);

  const onSubmit: SubmitHandler<RoomConferenceSchemaType> = (
    data: RoomConferenceSchemaType,
  ) => {
    const convertedFormData = convertSingleDocumentData(data);

    const __data = {
      officeId: Number(convertedFormData?.officeId),
      roomId: Number(convertedFormData?.roomId),
      buildingId: Number(convertedFormData?.buildingId),
      meetingPurpose: convertedFormData?.meetingPurpose,
      refreshmentDetails: convertedFormData?.refreshmentDetails,
      totalAttendance: Number(convertedFormData?.totalAttendance),
      bookStartTime: `${convertedFormData?.bookingStartDate}:00`,
      bookEndTime: `${convertedFormData?.bookingEndDate}:00`,
      document: convertedFormData?.document,
    };

    createRoomConference(__data as CreateConferenceRoomType);
  };

  const closeErrorModal = () => setIsErrorModalOpen(false);
  const successAction = () => navigate(CONFERENCE_ROOM);
  const cancelAction = () => navigate(CONFERENCE_ROOM);

  useEffect(() => {
    const reset = () => {
      setIsErrorModalOpen(false);
      setIsSuccessModalOpen(false);
      setCreateErrorMessage("");
    };
    return reset();
  }, []);

  useEffect(() => {
    if (createSuccess) {
      setIsSuccessModalOpen(true);
    }
    if (createError) {
      setIsErrorModalOpen(true);
      if (createError.response && createError.response.data) {
        setCreateErrorMessage(
          createError.response.data.message || "something was wrong",
        );
      }
    }
  }, [createSuccess, createError]);

  useEffect(() => {
    if (watchOffice) {
      setBuildingQueries({
        officeId: watchOffice,
      });
    }
  }, [watchOffice, setBuildingQueries]);

  useEffect(() => {
    if (watchBuilding) {
      setRoomQueries({
        buildingId: watchBuilding,
        roomTypeId: CONFERENCE_ROOM_ID,
      });
    }
  }, [watchBuilding, setRoomQueries]);

  return (
    <div>
      <>
        <SuccessModal isModalOpen={isSuccessModalOpen} action={successAction} />
        <ErrorModal
          subtitle={createErrorMessage}
          isModalOpen={isErrorModalOpen}
          closeModal={closeErrorModal}
        />
      </>
      <FormProvider {...method}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="container d-flex flex-column gap-6 border-bottom mt-10">
            <SelectControllerRow
              label="INVENTORY_ROOM_CONFERENCE.FORM.OFFICE"
              name={ROOM_CONFERENCE.OFFICE}
              register={{ ...register(ROOM_CONFERENCE.OFFICE) }}
              options={getConvertedOfficeAddress(office, language)}
              setValue={setValue}
              control={control}
              error={errors}
              required
            />

            <SelectControllerRow
              label="INVENTORY_ROOM_CONFERENCE.FORM.BUILDING"
              name={ROOM_CONFERENCE.BUILDING}
              register={{ ...register(ROOM_CONFERENCE.BUILDING) }}
              options={getConvertedMetaData(building)}
              setValue={setValue}
              control={control}
              error={errors}
              required
            />

            <SelectControllerRow
              label="INVENTORY_ROOM_CONFERENCE.FORM.CONFERENCE_ROOM"
              name={ROOM_CONFERENCE.ROOM}
              register={{ ...register(ROOM_CONFERENCE.ROOM) }}
              options={getConvertedMetaData(room)}
              setValue={setValue}
              control={control}
              error={errors}
              required
            />

            <InputTextRow
              label="INVENTORY_ROOM_CONFERENCE.FORM.MEETING_PURPOSE"
              register={{ ...register(ROOM_CONFERENCE.MEETING_PURPOSE) }}
              error={errors as any}
              required
            />

            <div className="row">
              <div className="col-lg-3">
                <Text weight="semibold" color="title" size="sm">
                  {t("INVENTORY_ROOM_CONFERENCE.FORM.MEETING_START_DATE.LABEL")}
                </Text>
                <span className="text-danger">&#42;</span>
              </div>
              <div className="col-lg-7">
                <Controller
                  control={control}
                  name={ROOM_CONFERENCE.BOOKING_START_DATE}
                  render={({ field }) => (
                    <DateInput
                      minimumDate={dayjs()}
                      maximumDate={dayjs(`${endDate}`)}
                      {...register(ROOM_CONFERENCE.BOOKING_START_DATE)}
                      format="YYYY-MM-DD HH:mm"
                      isTimePicker
                      name={ROOM_CONFERENCE.BOOKING_START_DATE}
                      onSelectDate={(date) => {
                        field.onChange(date);
                      }}
                      defaultValue={field.value as string}
                      placeholder={t(
                        "INVENTORY_ROOM_CONFERENCE.FORM.MEETING_START_DATE.PLACEHOLDER",
                      )}
                      prefix={<IconCalendar size="20" fill="subtitle2" />}
                    />
                  )}
                />
                <Text size="sm" color="danger" className="pt-5">
                  {errors[ROOM_CONFERENCE.BOOKING_START_DATE]?.message}
                </Text>
              </div>
            </div>

            <div className="row">
              <div className="col-lg-3">
                <Text weight="semibold" color="title" size="sm">
                  {t("INVENTORY_ROOM_CONFERENCE.FORM.MEETING_END_DATE.LABEL")}
                </Text>
                <span className="text-danger">&#42;</span>
              </div>
              <div className="col-lg-7">
                <Controller
                  control={control}
                  name={ROOM_CONFERENCE.BOOKING_END_DATE}
                  render={({ field }) => (
                    <DateInput
                      minimumDate={
                        dayjs().isAfter(`${startDate}`)
                          ? dayjs()
                          : dayjs(`${startDate}`)
                      }
                      {...register(ROOM_CONFERENCE.BOOKING_END_DATE)}
                      format="YYYY-MM-DD HH:mm"
                      isTimePicker
                      name={ROOM_CONFERENCE.BOOKING_END_DATE}
                      onSelectDate={(date) => {
                        field.onChange(date);
                      }}
                      defaultValue={field.value as string}
                      placeholder={t(
                        "INVENTORY_ROOM_CONFERENCE.FORM.MEETING_END_DATE.PLACEHOLDER",
                      )}
                      prefix={<IconCalendar size="20" fill="subtitle2" />}
                    />
                  )}
                />
                <Text size="sm" color="danger" className="pt-10">
                  {errors[ROOM_CONFERENCE.BOOKING_END_DATE]?.message}
                </Text>
              </div>
            </div>

            <InputTextRow
              label="INVENTORY_ROOM_CONFERENCE.FORM.TOTAL_ATTENDEES"
              register={{ ...register(ROOM_CONFERENCE.TOTAL_ATTENDEES) }}
              error={errors as any}
              required
            />

            <InputTextAreaRow
              label="INVENTORY_ROOM_CONFERENCE.FORM.REFRESHMENT_SUPPORT"
              register={{ ...register(ROOM_CONFERENCE.REFRESHMENT_DETAILS) }}
            />
            <FileRow
              name={ROOM_CONFERENCE.DOCUMENT}
              control={control}
              setValue={setValue}
              register={register}
              error={errors}
              fileDetails={["documentName", "date", "remarks", "memorialNo"]}
            />

            <div className="row">
              <div className="col-lg-10">
                <div className="d-flex justify-content-end gap-5">
                  <Button
                    fill="outline"
                    size="sm"
                    type="light"
                    htmlType="button"
                    onClick={cancelAction}
                  >
                    {t("INVENTORY_ROOM_CONFERENCE.FORM.CANCEL_BUTTON_TEXT")}
                  </Button>
                  <Button fill="fill" size="sm" type="info" htmlType="submit">
                    {t("INVENTORY_ROOM_CONFERENCE.FORM.SAVE_BUTTON_TEXT")}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </form>
      </FormProvider>
    </div>
  );
}
